import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';

import { Button } from '#/modules/commonComponents/button';
import BomPickerDialog from '#/modules/momMgmt/settings/bom/components/BomPickerDialog';
import { formatBomSummaryLabel } from '#/modules/momMgmt/settings/bom/helper/syncBomLinkedMaterial';

/**
 * 物料表單：關聯 BOM（單選 ACTIVE；可清除）。
 */
const MtrBomLinkSection = ({ control, setValue, disabled = false }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const bomId = useWatch({ control, name: 'bomId', defaultValue: '' });
  const bomCode = useWatch({ control, name: 'bomCode', defaultValue: '' });
  const bomName = useWatch({ control, name: 'bomName', defaultValue: '' });
  const bomVersion = useWatch({ control, name: 'bomVersion', defaultValue: '' });

  const summary = useMemo(
    () => formatBomSummaryLabel({ code: bomCode, name: bomName, version: bomVersion }),
    [bomCode, bomName, bomVersion],
  );

  const initialSelectedRow = useMemo(() => {
    if (!bomId) {
      return null;
    }
    return {
      id: bomId,
      code: bomCode,
      name: bomName,
      version: bomVersion,
    };
  }, [bomId, bomCode, bomName, bomVersion]);

  function handleOpenPicker() {
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  function handleClear() {
    setValue('bomId', '', { shouldDirty: true });
    setValue('bomCode', '', { shouldDirty: true });
    setValue('bomName', '', { shouldDirty: true });
    setValue('bomVersion', '', { shouldDirty: true });
  }

  function handlePickerConfirm(row) {
    if (!row?.id) {
      return;
    }
    setValue('bomId', row.id, { shouldDirty: true });
    setValue('bomCode', row.code ?? '', { shouldDirty: true });
    setValue('bomName', row.name ?? '', { shouldDirty: true });
    setValue('bomVersion', row.version ?? '', { shouldDirty: true });
  }

  return (
    <section className="form-section relative w-full pb-5">
      <div className="form-section__header">
        <h2 className="form-block__title mb-0! mr-2">關聯 BOM</h2>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            disabled={disabled}
            onClick={handleOpenPicker}
          >
            選擇 BOM
          </Button>
          {bomId ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={handleClear}
            >
              清除關聯
            </Button>
          ) : null}
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        一對一；僅可選啟用中的 BOM。儲存時寫入物料
        {' '}
        <code className="text-xs">bomId</code>
        ，不經 BOM mutation。
      </p>
      <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
        {bomId ? (
          <p className="text-sm text-slate-800">{summary}</p>
        ) : (
          <p className="text-sm text-muted-foreground">尚未關聯 BOM</p>
        )}
      </div>

      <BomPickerDialog
        key={pickerKey}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedId={bomId || null}
        initialSelectedRow={initialSelectedRow}
        onConfirm={handlePickerConfirm}
      />
    </section>
  );
};

MtrBomLinkSection.propTypes = {
  control: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default MtrBomLinkSection;
